// frontend/src/components/admin/MidiasList.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { API_URL } from "../../apiConfig";
import { getFreshAccessToken } from "../../auth";
import "./midias.css";

// ── Icon (Lucide-style SVG, from components.jsx) ─────────────────────────────
const Icon = ({ name, size = 18, stroke = 1.6, color = "currentColor" }) => {
  const paths = {
    home: <><path d="M3 11.5L12 4l9 7.5"/><path d="M5 10v9a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1v-9"/></>,
    zap: <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/>,
    list: <><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></>,
    plus: <><path d="M12 5v14"/><path d="M5 12h14"/></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></>,
    file: <><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z"/><path d="M14 3v6h6"/></>,
    search: <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    arrowDown: <><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></>,
    arrowRight: <><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></>,
    x: <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    alert: <><circle cx="12" cy="12" r="9"/><path d="M12 8v4"/><path d="M12 16h.01"/></>,
    eye: <><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></>,
    edit: <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    download: <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></>,
    refresh: <><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10"/><path d="M20.49 15a9 9 0 01-14.85 3.36L1 14"/></>,
    filter: <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>,
    layoutGrid: <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></>,
    trash: <><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>,
    rotate: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></>,
    upload: <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></>,
    play: <path d="M6 4l14 8-14 8z"/>,
    film: <><rect x="2" y="2" width="20" height="20" rx="2"/><path d="M7 2v20M17 2v20M2 12h20M2 7h5M17 7h5M2 17h5M17 17h5"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
      {paths[name] || null}
    </svg>
  );
};

// ── DetailGrid ────────────────────────────────────────────────────────────────
const DetailGrid = ({ rows }) => (
  <div style={{
    display: "grid", gridTemplateColumns: "auto 1fr",
    gap: "8px 16px", padding: "12px 14px",
    background: "var(--surface-sunken)",
    border: "0.5px solid var(--hairline)", borderRadius: 10, fontSize: 12.5,
  }}>
    {rows.map(([label, value], i) => (
      <React.Fragment key={i}>
        <span style={{ color: "var(--fg-3)", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontWeight: 500, color: "var(--fg)" }}>{value ?? "—"}</span>
      </React.Fragment>
    ))}
  </div>
);

// ── UploadField ───────────────────────────────────────────────────────────────
const UploadField = ({ label, required, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}{required && <span style={{ color: "var(--red)", marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.4 }}>{hint}</div>}
  </div>
);

// ── PreviewRow ────────────────────────────────────────────────────────────────
const PreviewRow = ({ k, v }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
    <span style={{ color: "var(--fg-3)" }}>{k}</span>
    <span style={{ fontWeight: 500, color: "var(--fg)", textAlign: "right" }}>{v}</span>
  </div>
);

// ── UploadPreview ─────────────────────────────────────────────────────────────
// Réplica do player (tv.html/tv.css/tv.js) no viewport CSS real das TVs (~540×960, ver
// diagnostics → viewport), reduzida com CSS transform. Os valores em px/vw do tv.css entram
// literalmente — altere aqui se mudar o player.
//   Tela Cheia → comunicado só-mídia no layout4: preenche a tela acima do rodapé (object-fit: cover)
//   Meia Tela  → mídia de campanha no layout2: quadrado 100vw (object-fit: contain) + card do comunicado
//   Footer Bar → .footer-bar: largura total, altura pela proporção da imagem
const TV_VW = 540, TV_VH = 960;

// Rodapé real de uma tela cadastrada (o player não tem rodapé padrão) — buscado uma vez
let screenFooterPromise = null;
function loadScreenFooterUrl() {
  if (!screenFooterPromise) {
    screenFooterPromise = fetch(`${API_URL}/admin/screens`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d?.screens || []).find((s) => s.footerMediaUrl)?.footerMediaUrl || null)
      .catch(() => { screenFooterPromise = null; return null; });
  }
  return screenFooterPromise;
}

const UploadPreview = ({ formato, tipo, nome, parceiro, arquivo, rotacao, publicUrl = null, width = 200 }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [screenFooterUrl, setScreenFooterUrl] = useState(null);

  useEffect(() => {
    if (!arquivo) { setMediaUrl(null); return; }
    const url = URL.createObjectURL(arquivo);
    setMediaUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  useEffect(() => {
    let alive = true;
    loadScreenFooterUrl().then((u) => { if (alive) setScreenFooterUrl(u); });
    return () => { alive = false; };
  }, []);

  const displayUrl = mediaUrl || publicUrl;
  const isVideo = tipo === "Vídeo";
  const isHalf = formato === "Meia Tela";
  const isFooter = formato === "Footer Bar";
  const isFull = !isHalf && !isFooter;
  const footerUrl = isFooter ? (displayUrl || screenFooterUrl) : screenFooterUrl;

  const vw = (n) => TV_VW * n / 100;
  const clamp = (min, v, max) => Math.min(max, Math.max(min, v));

  // Device: tela 9:16 exata, igual à da TV
  const BEZEL = 5;
  const innerW = Math.max(120, Math.floor(width) - BEZEL * 2);
  const innerH = Math.round(innerW * TV_VH / TV_VW);
  const PS = innerW / TV_VW;

  // Relógio (tv.js: updateClock) — "Ter. 13 de out."
  const now = new Date();
  const clockTime = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hour12: false }).format(now);
  const clockDate = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", weekday: "short", day: "numeric", month: "short" }).format(now)
    .replace(/^(\w+)[,.]?\s+/, (m, w) => `${w.charAt(0).toUpperCase()}${w.slice(1).toLowerCase()}. `)
    .replace(/(\w{3})$/, "$1.");
  // layout4 (Tela Cheia) usa o relógio claro; layout2 o cinza translúcido
  const clockTheme = isFull
    ? { bg: "rgba(255,255,255,0.55)", border: "1.5px solid rgba(0,0,0,0.18)", shadow: "none", time: "rgba(55,55,62,0.75)", timeShadow: "none", date: "rgba(75,75,83,0.65)", dateShadow: "none" }
    : { bg: "rgba(90,90,90,0.50)", border: "1px solid rgba(255,255,255,0.25)", shadow: "0 2px 10px rgba(0,0,0,0.25)", time: "#ffffff", timeShadow: "0 2px 6px rgba(0,0,0,0.45)", date: "#f0f0f0", dateShadow: "0 1px 3px rgba(0,0,0,0.4)" };

  const placeholder = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
      <span style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={isVideo ? "play" : "image"} size={24} color="rgba(255,255,255,0.6)"/>
      </span>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 16, fontStyle: "italic" }}>selecione {isVideo ? "um vídeo" : "uma imagem"}</div>
    </div>
  );
  const renderMedia = (fit) => (
    isVideo
      ? <video key={displayUrl} src={displayUrl} style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }} muted autoPlay loop playsInline/>
      : <img src={displayUrl} alt="preview" style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }}/>
  );

  const label = isHalf ? "MEIA TELA" : isFooter ? "FOOTER BAR" : "TELA CHEIA";
  const caption = isHalf
    ? "mídia de campanha · quadrado no topo (Retrato Padrão)"
    : isFooter
      ? "barra inferior · largura total"
      : <>rotação: <b style={{ color: "var(--fg-2)" }}>{rotacao}°</b> → efetiva: <b style={{ color: "var(--fg-2)" }}>{((rotacao % 360) + 360) % 360}°</b></>;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: "white", background: isFull ? "var(--accent)" : "#374151", padding: "3px 10px", borderRadius: 4, letterSpacing: "0.08em" }}>{label}</span>
      <div style={{ width: innerW + BEZEL * 2, height: innerH + BEZEL * 2, boxSizing: "border-box", background: "#000", border: `${BEZEL}px solid #0a0a0c`, borderRadius: 14, overflow: "hidden", position: "relative", boxShadow: "0 12px 32px rgba(0,0,0,0.25)", flexShrink: 0 }}>
        {/* Palco virtual no viewport da TV, depois reduzido */}
        <div style={{ width: TV_VW, height: TV_VH, transform: `scale(${PS})`, transformOrigin: "top left", position: "absolute", top: 0, left: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#000", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif", WebkitFontSmoothing: "antialiased" }}>
          {/* Relógio (.clock) */}
          <div style={{ position: "absolute", top: 20, right: 30, zIndex: 1000, textAlign: "right", lineHeight: 1.2, background: clockTheme.bg, border: clockTheme.border, boxShadow: clockTheme.shadow, padding: "8px 14px", borderRadius: 12, pointerEvents: "none" }}>
            <span style={{ display: "block", fontSize: clamp(18, vw(3), 32), fontWeight: 700, lineHeight: 1, letterSpacing: "0.5px", color: clockTheme.time, textShadow: clockTheme.timeShadow }}>{clockTime}</span>
            <span style={{ display: "block", fontSize: clamp(6, vw(1.1), 10), fontWeight: 500, marginTop: 3, letterSpacing: "0.2px", whiteSpace: "nowrap", opacity: 0.8, color: clockTheme.date, textShadow: clockTheme.dateShadow }}>{clockDate}</span>
          </div>

          {/* .app: altura = tela − rodapé */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: isFull ? "#ffffff" : "#000" }}>
            {isFull ? (
              // Comunicado só-mídia: card sem título nem padding, mídia em cover (ExoPlayer: zoom)
              <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: displayUrl ? "#ffffff" : `linear-gradient(160deg, ${parceiro?.cor || "#1f3a7a"}cc 0%, #0a0a0c 70%)` }}>
                {displayUrl ? renderMedia("cover") : placeholder}
              </div>
            ) : (
              <>
                {/* .media-area: quadrado 100vw, mídia em contain */}
                <div style={{ flex: "0 0 auto", width: TV_VW, height: TV_VW, background: isHalf ? "#000" : "linear-gradient(160deg, #1a1a2e 0%, #0a0a0c 100%)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", zIndex: 50 }}>
                  {isHalf && displayUrl ? renderMedia("contain") : isHalf ? placeholder : (
                    <span style={{ color: "#2a2a2a", fontSize: 22, textAlign: "center", lineHeight: 1.6, whiteSpace: "pre-line" }}>{"conteúdo\ndo parceiro"}</span>
                  )}
                </div>
                {/* .notices-area + card (layout2: cantos 37px, sobe 30px sobre a mídia) */}
                <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: "#ffffff", position: "relative", zIndex: 100 }}>
                  <div style={{ flex: 1, minHeight: 0, overflow: "hidden", borderRadius: "37px 37px 0 0", marginTop: -30, boxShadow: "0 24px 64px rgba(0,0,0,0.25), 0 12px 32px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.12)", background: "linear-gradient(180deg, #ffffff 0%, #f3f5f8 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: "#ccc", fontSize: 16, fontStyle: "italic" }}>comunicado</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* .footer-bar: largura total, altura pela proporção da imagem */}
          {footerUrl ? (
            <div style={{ flexShrink: 0, lineHeight: 0, fontSize: 0, position: "relative", zIndex: 9999 }}>
              <img src={footerUrl} alt="" style={{ width: "100%", height: "auto", display: "block" }}/>
            </div>
          ) : isFooter && (
            <div style={{ flexShrink: 0, height: 60, background: "#1a1a2e", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.5)", fontSize: 16, fontStyle: "italic" }}>selecione uma imagem</div>
          )}
        </div>
      </div>
      <div style={{ fontSize: 10, color: "var(--fg-3)" }}>{caption}</div>
    </div>
  );
};

// ── Data helpers ──────────────────────────────────────────────────────────────
function deriveStatus(m) {
  if (!m.active) return "inativo";
  const now = new Date();
  if (m.validTo && new Date(m.validTo) < now) return "expirada";
  if (m.validFrom && new Date(m.validFrom) > now) return "agendada";
  return "ativo";
}

function deriveFormato(m) {
  // Tela Cheia = portrait 9:16 (ex: 1080×1920 → altura > largura)
  if (!m.width || !m.height) return "Tela Cheia";
  if (m.height > m.width) return "Tela Cheia";
  if (m.width / m.height >= 9) return "Footer Bar"; // ex: 1080×120 → razão 9:1
  return "Meia Tela";
}

function mapAsset(m, pMap, usageMap) {
  const partner = m.partnerId ? pMap[String(m.partnerId?._id || m.partnerId)] : null;
  const status  = deriveStatus(m);
  const formato = deriveFormato(m);
  const dim     = m.width && m.height ? `${m.width}×${m.height}` : "—";
  return {
    _id:         String(m._id),
    activeRaw:   !!m.active,
    nome:        m.name || "Sem nome",
    parceiro:    partner?.company || partner?.companyName || null,
    parceiroLogo: partner ? (partner.company || partner.companyName || "?").replace(/\s+/g,"").slice(0,2).toUpperCase() : null,
    parceiroCor: partner?.color || partner?.brandColor || null,
    tipo:        m.type === "VIDEO" ? "Vídeo" : "Imagem",
    orient:      m.rotation || 0,
    dur:         m.durationSec || 0,
    status,
    formato,
    dim,
    criado: new Date(m.createdAt).toLocaleDateString("pt-BR"),
    fim:    m.validTo ? new Date(m.validTo).toLocaleDateString("pt-BR") : null,
    usos:   usageMap[String(m._id)] || [],
    publicUrl: m.publicUrl,
    width:      m.width || null,
    height:     m.height || null,
    compatibleLayouts: m.compatibleLayouts || [],
    origem:     m.source === "CONDO" ? "condo" : "admin",
    condoName:  m.condoName || null,
  };
}

const FORMATOS = ["Todos os formatos", "Tela Cheia", "Meia Tela", "Footer Bar"];

function statusLabel(s) {
  return s === "ativo" ? "Ativo" : s === "agendada" ? "Agendada" : s === "inativo" ? "Inativo" : "Expirada";
}
function statusBadgeClass(s) {
  return s === "ativo" ? "badge-green" : s === "agendada" ? "badge-amber" : "badge-gray";
}

function classifyLayouts(width, height) {
  if (!width || !height || width <= 0 || height <= 0) return [];
  if (height > width) return ["layout4"];
  if (width > height) return ["layout2", "layout3"];
  return ["layout2", "layout3"];
}

function renderLayoutBadge(layouts) {
  const base = { alignSelf: "flex-start", fontSize: 10, padding: "2px 7px" };
  if (!layouts || layouts.length === 0) {
    return <span className="badge badge-gray" style={base}>Não classificado</span>;
  }
  const portrait = layouts.includes("layout4") && !layouts.includes("layout2");
  const landscape = !layouts.includes("layout4") && layouts.length > 0;
  if (portrait) return <span className="badge badge-blue" style={base}>Tela Cheia</span>;
  if (landscape) return <span className="badge badge-green" style={base}>Meia Tela</span>;
  return <span className="badge badge-gray" style={base}>Todos</span>;
}

// ── Thumbnail ─────────────────────────────────────────────────────────────────
const MidiaThumb = ({ m, size = 50 }) => {
  // Deriva aspect ratio das dimensões reais (ex: "1080×1920" → 0.5625)
  const [dw, dh] = (m.dim && m.dim !== "—") ? m.dim.split("×").map(Number) : [null, null];
  const ratio = (dw && dh && dh > 0) ? dw / dh : (m.formato === "Tela Cheia" ? 9/16 : m.formato === "Footer Bar" ? 9/1 : 1);
  // Encaixa em size×size mantendo proporção (contain)
  const w = ratio >= 1 ? size : Math.round(size * ratio);
  const h = ratio >= 1 ? Math.round(size / ratio) : size;
  const bg = m.parceiroCor ? `${m.parceiroCor}22` : "var(--surface-sunken)";
  return (
    <div style={{ width: w, height: h, borderRadius: 6, overflow: "hidden", border: "0.5px solid var(--hairline)", flexShrink: 0, background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {m.tipo === "Imagem" && m.publicUrl ? (
        <img src={m.publicUrl} alt={m.nome} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }}/>
      ) : (
        <Icon name={m.tipo === "Vídeo" ? "play" : "image"} size={ratio < 1 ? 16 : 12} color="var(--fg-4)"/>
      )}
    </div>
  );
};

// ── Grid Card ─────────────────────────────────────────────────────────────────
const MidiaCard = ({ m, onClick }) => (
  <button onClick={onClick} style={{ textAlign: "left", background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: "var(--r-md)", padding: 12, cursor: "default", font: "inherit", color: "inherit", display: "flex", flexDirection: "column", gap: 10, transition: "box-shadow .15s ease" }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = "var(--shadow-md)"}
    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
  >
    <div style={{ aspectRatio: m.formato === "Footer Bar" ? "9 / 1" : m.formato === "Meia Tela" ? "16 / 9" : "9 / 16", maxHeight: m.formato === "Footer Bar" ? 40 : m.formato === "Meia Tela" ? 80 : 200, borderRadius: 8, overflow: "hidden", border: "0.5px solid var(--hairline)", alignSelf: "center", width: m.formato !== "Tela Cheia" ? "100%" : "auto", position: "relative", background: m.parceiroCor ? `${m.parceiroCor}22` : "var(--surface-sunken)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {m.tipo === "Imagem" && m.publicUrl && <img src={m.publicUrl} alt={m.nome} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>}
      {m.tipo === "Vídeo" && !m.publicUrl && <Icon name="play" size={20} color="var(--fg-4)"/>}
      <span className={`badge ${statusBadgeClass(m.status)}`} style={{ position: "absolute", top: 6, left: 6, fontSize: 9 }}>
        <span className="badge-dot"/>
        {m.status === "ativo" ? "ON" : m.status === "agendada" ? "AG" : "EX"}
      </span>
    </div>
    <div>
      <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.nome}</div>
      <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 2 }}>{m.parceiro || "(sem parceiro)"} · {m.dur}s</div>
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span className="badge badge-blue" style={{ fontSize: 10 }}>{m.formato}</span>
      <span style={{ fontSize: 10.5, color: "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>{m.dim}</span>
    </div>
  </button>
);

// ── Detail Drawer ─────────────────────────────────────────────────────────────
const MidiaDetalheDrawer = ({ m, onClose, onDelete, onEdit }) => {
  if (!m) return null;
  return (
    <div className="midias-overlay" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.28)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "flex-end", animation: "midias-scrim-in .2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(540px, 92vw)", height: "100%", background: "var(--surface)", borderLeft: "0.5px solid var(--hairline-strong)", padding: 24, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18, animation: "midias-drawer-in .35s cubic-bezier(.2,.9,.3,1)" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Mídia indoor</div>
            <h2 style={{ margin: "4px 0 6px", fontSize: 22, letterSpacing: "-0.02em", color: "var(--fg)" }}>{m.nome}</h2>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span className={`badge ${statusBadgeClass(m.status)}`}>
                <span className="badge-dot"/>{statusLabel(m.status)}
              </span>
              <span className="badge badge-blue">{m.formato}</span>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16}/></button>
        </div>

        {/* Preview */}
        <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}>
          <UploadPreview
            formato={m.formato}
            tipo={m.tipo}
            nome={m.nome}
            parceiro={{ cor: m.parceiroCor, logo: m.parceiroLogo, nome: m.parceiro }}
            arquivo={null}
            rotacao={m.orient}
            publicUrl={m.publicUrl}
            width={260}
          />
        </div>

        <DetailGrid rows={[
          ["Tipo",       `${m.tipo} · ${m.orient}°`],
          ["Duração",    `${m.dur} segundos`],
          ["Formato",    m.formato],
          ["Dimensão",   m.dim],
          ["Parceiro",   m.parceiro || "(sem parceiro)"],
          ["Origem",     m.origem === "condo" ? `Síndico${m.condoName ? ` · ${m.condoName}` : ""}` : "Admin"],
          ["Criado em",  m.criado],
          ["Data fim",   m.fim || "Sem expiração"],
          ["Status",     statusLabel(m.status)],
        ]}/>

        {m.usos.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Onde está sendo usada</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {m.usos.map((u, i) => {
                const isFooter = u.kind === "footer";
                const isCampaign = !isFooter && (u.kind === "campaign" || u.campaignName);
                const sn = u.screenNames || [];
                const title = isFooter ? "Rodapé das telas" : isCampaign ? (u.campaignName || "Campanha") : (u.noticeTitle || "Comunicado");
                const scope = isFooter
                  ? sn.join(", ")
                  : isCampaign
                  ? (sn.length === 0 ? "—" : sn[0] === "Todas as telas" ? "Todas as telas" : /condomínio|cidade/.test(sn[0]) ? sn[0] : `${sn.length} tela${sn.length !== 1 ? "s" : ""}`)
                  : (u.scopeLabel || "—");
                return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)", borderRadius: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                        <Icon name={isFooter ? "image" : isCampaign ? "zap" : "file"} size={12} color={isCampaign ? "var(--accent)" : "var(--fg-3)"}/>
                        {title}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 3, paddingLeft: 18 }}>{scope}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: "auto", paddingTop: 12 }}>
          <button className="btn btn-primary" style={{ justifyContent: "center", background: "var(--purple)", borderColor: "var(--purple)" }} onClick={() => onEdit(m)}>
            <Icon name="edit" size={14}/> Editar
          </button>
          {m.publicUrl && (
            <a href={m.publicUrl} target="_blank" rel="noreferrer" className="btn" style={{ justifyContent: "center", textDecoration: "none" }}>
              <Icon name="eye" size={14}/> Visualizar
            </a>
          )}
          {m.publicUrl && (
            <button className="btn" style={{ justifyContent: "center" }} onClick={async () => {
              try {
                const res = await fetch(m.publicUrl);
                const blob = await res.blob();
                const ext = m.tipo === "Vídeo" ? ".mp4" : (blob.type.includes("png") ? ".png" : ".jpg");
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${m.nome.replace(/\s+/g, "_")}${ext}`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
              } catch (e) {
                window.open(m.publicUrl, "_blank");
              }
            }}>
              <Icon name="download" size={14}/> Baixar
            </button>
          )}
          <button className="btn" style={{ justifyContent: "center", color: "var(--red)", borderColor: "var(--red-soft)" }} onClick={() => onDelete(m._id)}>
            <Icon name="trash" size={14}/> Excluir
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Upload Modal ──────────────────────────────────────────────────────────────
const UploadMidiaModal = ({ open, onClose, onSuccess, partners, token, edit = null }) => {
  const isEdit = !!edit;
  // Espaço livre da coluna do preview — a TV de mentira cresce até preenchê-lo, sem rolar
  const [previewBox, setPreviewBox] = useState({ w: 0, h: 0 });
  const previewObserver = React.useRef(null);
  const previewAreaRef = useCallback((el) => {
    if (previewObserver.current) { previewObserver.current.disconnect(); previewObserver.current = null; }
    if (!el || typeof ResizeObserver === "undefined") return;
    previewObserver.current = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      setPreviewBox((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    previewObserver.current.observe(el);
  }, []);
  const [nome,          setNome]         = useState("");
  const [tipo,          setTipo]         = useState("Imagem");
  const [formato,       setFormato]      = useState("Tela Cheia");
  const [parceiro,      setParceiro]     = useState("");
  const [parceiroBusca, setParceiroBusca]= useState("");
  const [duracao,       setDuracao]      = useState(15);
  const [rotacao,       setRotacao]      = useState(270);
  const [dataFim,       setDataFim]      = useState("");
  const [ativo,         setAtivo]        = useState(true);
  const [arquivo,       setArquivo]      = useState(null);
  const [trocarArquivo, setTrocarArquivo]= useState(false);
  const [uploading,     setUploading]    = useState(false);
  const [progress,      setProgress]     = useState(0);

  // Pre-fill / reset when modal opens
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setNome(edit.nome || "");
      setTipo(edit.tipo || "Imagem");
      setFormato(edit.formato || "Tela Cheia");
      setDuracao(edit.dur || 15);
      setRotacao(edit.orient ?? 270);
      setAtivo(edit.status !== "inativo");
      setArquivo(null);
      setTrocarArquivo(false);
      // Pre-select partner by matching name → find _id
      if (edit.parceiro) {
        const found = partners.find(p => (p.company || p.companyName) === edit.parceiro);
        setParceiro(found ? String(found._id) : "");
      } else { setParceiro(""); }
      setParceiroBusca("");
      if (edit.fim) {
        const [d, mo, y] = edit.fim.split("/");
        setDataFim(`${y}-${mo}-${d}`);
      } else { setDataFim(""); }
    } else {
      setNome(""); setTipo("Imagem"); setFormato("Tela Cheia");
      setParceiro(""); setParceiroBusca("");
      setDuracao(15); setRotacao(270); setDataFim("");
      setAtivo(true); setArquivo(null); setTrocarArquivo(false);
    }
  }, [open, edit]); // eslint-disable-line

  const efetiva = ((rotacao % 360) + 360) % 360;
  const showDropzone = !isEdit || trocarArquivo;

  const parceirosFiltrados = partners.filter(p =>
    (p.company || p.companyName || "").toLowerCase().includes(parceiroBusca.toLowerCase())
  );
  const parceiroSel = partners.find(p => String(p._id) === parceiro);
  const parceiroSelDisplay = parceiroSel
    ? { nome: parceiroSel.company || parceiroSel.companyName, logo: (parceiroSel.company || parceiroSel.companyName || "?").replace(/\s+/g,"").slice(0,2).toUpperCase(), cor: parceiroSel.brandColor || "#1f3a7a" }
    : null;

  async function handleSaveEdit() {
    setUploading(true); setProgress(0);
    try {
      if (arquivo) {
        // New file → upload replacing
        const fd = new FormData();
        fd.append("file", arquivo);
        fd.append("type",        tipo === "Vídeo" ? "VIDEO" : "IMAGE");
        fd.append("rotation",    String(rotacao));
        fd.append("active",      String(ativo));
        fd.append("name",        nome.trim());
        fd.append("durationSec", String(duracao));
        if (dataFim)   fd.append("validTo",   dataFim);
        if (parceiro)  fd.append("partnerId", parceiro);
        const authToken = await getFreshAccessToken();
        if (!authToken) throw new Error("Sessão expirada — faça login novamente.");
        await new Promise((res, rej) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener("progress", e => { if (e.lengthComputable) setProgress(Math.round(e.loaded / e.total * 90)); });
          xhr.addEventListener("load", () => { xhr.status < 300 ? res() : rej(new Error(JSON.parse(xhr.responseText)?.message || `Erro ${xhr.status}`)); });
          xhr.addEventListener("error", () => rej(new Error("Erro de rede")));
          xhr.open("PUT", `${API_URL}/admin/media-assets/${edit._id}/file`);
          xhr.setRequestHeader("Authorization", `Bearer ${authToken}`);
          xhr.send(fd);
        });
      } else {
        // Metadata only → PATCH
        const body = {
          name:        nome.trim(),
          durationSec: duracao,
          rotation:    rotacao,
          active:      ativo,
          validTo:     dataFim || null,
        };
        if (parceiro) body.partnerId = parceiro;
        const r = await fetch(`${API_URL}/admin/media-assets/${edit._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error((await r.json()).message || `Erro ${r.status}`);
      }
      setProgress(100);
      onSuccess();
      onClose();
    } catch (e) { alert(e.message || "Erro ao salvar"); }
    finally { setUploading(false); setProgress(0); }
  }

  async function handleUpload() {
    if (!arquivo) { alert("Selecione um arquivo"); return; }
    setUploading(true); setProgress(0);
    try {
      let width = null, height = null;
      try {
        ({ width, height } = await new Promise((res, rej) => {
          if (arquivo.type.startsWith("image/")) {
            const img = new Image();
            img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => res({ width: null, height: null });
            img.src = URL.createObjectURL(arquivo);
          } else if (arquivo.type.startsWith("video/")) {
            const vid = document.createElement("video");
            vid.onloadedmetadata = () => res({ width: vid.videoWidth || null, height: vid.videoHeight || null });
            vid.onerror = () => res({ width: null, height: null });
            vid.src = URL.createObjectURL(arquivo); vid.load();
          } else res({ width: null, height: null });
        }));
      } catch {}

      const fd = new FormData();
      fd.append("file", arquivo);
      fd.append("type", tipo === "Vídeo" ? "VIDEO" : "IMAGE");
      fd.append("rotation", String(rotacao));
      fd.append("active", String(ativo));
      if (nome.trim())   fd.append("name",       nome.trim());
      if (duracao)       fd.append("durationSec", String(duracao));
      if (dataFim)       fd.append("validTo",     dataFim);
      if (parceiro)      fd.append("partnerId",   parceiro);
      if (width)         fd.append("width",       String(width));
      if (height)        fd.append("height",      String(height));

      const authToken = await getFreshAccessToken();
      if (!authToken) throw new Error("Sessão expirada — faça login novamente.");
      await new Promise((res, rej) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", e => { if (e.lengthComputable) setProgress(Math.round(e.loaded / e.total * 95)); });
        xhr.addEventListener("load", () => { xhr.status < 300 ? res() : rej(new Error(JSON.parse(xhr.responseText)?.message || `Erro ${xhr.status}`)); });
        xhr.addEventListener("error", () => rej(new Error("Erro de rede")));
        xhr.open("POST", `${API_URL}/admin/media-assets/upload`);
        xhr.setRequestHeader("Authorization", `Bearer ${authToken}`);
        xhr.send(fd);
      });

      setProgress(100);
      onSuccess();
      onClose();
    } catch (e) {
      alert(e.message || "Erro ao fazer upload");
    } finally {
      setUploading(false); setProgress(0);
    }
  }

  if (!open) return null;

  return (
    <div className="midias-overlay" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", animation: "midias-scrim-in .2s ease", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(960px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden", animation: "midias-pop-in .2s cubic-bezier(.2,.9,.3,1.2)" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: isEdit ? "var(--purple-soft)" : "var(--accent-soft)", color: isEdit ? "var(--purple)" : "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name={isEdit ? "edit" : "upload"} size={16}/>
            </span>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, letterSpacing: "-0.01em", color: "var(--fg)" }}>{isEdit ? "Editar mídia" : "Upload de mídia"}</h2>
              <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}>
                {isEdit ? `Alterando ${edit.nome} · ${edit.formato} · ${edit.dim}` : "Adicione uma imagem ou vídeo à biblioteca indoor"}
              </div>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16}/></button>
        </div>

        {/* Body: two columns */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr)", flex: 1, minHeight: 0, overflow: "hidden" }}>

          {/* Form */}
          <div style={{ padding: "20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>

            <UploadField label="Nome da mídia" required hint="Identificação interna — facilita a seleção nas campanhas.">
              <div className="input">
                <input type="text" placeholder="Ex: Vídeo Institucional, Banner Promoção…" value={nome} onChange={e => setNome(e.target.value)}/>
              </div>
            </UploadField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <UploadField label="Tipo" required>
                <div className="input">
                  <select value={tipo} onChange={e => setTipo(e.target.value)}>
                    <option>Imagem</option>
                    <option>Vídeo</option>
                  </select>
                  <Icon name="arrowDown" size={12} color="var(--fg-3)"/>
                </div>
              </UploadField>
              <UploadField label="Formato" required>
                <div className="input">
                  <select value={formato} onChange={e => setFormato(e.target.value)}>
                    <option>Tela Cheia</option>
                    <option>Meia Tela</option>
                    <option>Footer Bar</option>
                  </select>
                  <Icon name="arrowDown" size={12} color="var(--fg-3)"/>
                </div>
              </UploadField>
            </div>

            {/* Drop zone / arquivo */}
            <UploadField label="Arquivo" required={!isEdit} hint={formato === "Footer Bar" ? "Recomendado: 1080×120 (barra inferior)" : formato === "Meia Tela" ? "Recomendado: 1920×1080 (landscape)" : `Recomendado: 1080×1920 ${tipo === "Vídeo" ? "(MP4, até 50 MB)" : "(PNG ou JPG)"}`}>
              {isEdit && !trocarArquivo ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--accent-soft)", border: "0.5px solid var(--hairline-strong)", borderRadius: 10 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.6)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name={tipo === "Vídeo" ? "play" : "image"} size={14}/>
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: "var(--fg-3)", fontWeight: 500 }}>Arquivo atual</div>
                    <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{edit.publicUrl ? edit.publicUrl.split("/").pop() : edit.nome}</div>
                  </div>
                  <button onClick={() => setTrocarArquivo(true)} style={{ border: "0.5px solid var(--hairline)", background: "var(--surface)", color: "var(--fg)", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 500, cursor: "default", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Icon name="refresh" size={11}/> Substituir
                  </button>
                </div>
              ) : (
                <label style={{ border: `1.5px dashed ${arquivo ? "var(--accent)" : "var(--hairline-strong)"}`, borderRadius: 10, padding: arquivo ? "12px 14px" : "20px 14px", textAlign: "center", background: arquivo ? "var(--accent-soft)" : "var(--surface-sunken)", cursor: "pointer", display: "flex", flexDirection: arquivo ? "row" : "column", alignItems: "center", justifyContent: "center", gap: arquivo ? 12 : 6, transition: "all .15s ease" }}>
                  {arquivo ? (
                    <>
                      <span style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.6)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name={tipo === "Vídeo" ? "play" : "image"} size={16}/>
                      </span>
                      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{arquivo.name}</div>
                        <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{(arquivo.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                      <button onClick={e => { e.preventDefault(); setArquivo(null); if (isEdit) setTrocarArquivo(false); }} style={{ border: "none", background: "transparent", color: "var(--fg-3)", display: "flex", cursor: "default", padding: 4 }}>
                        <Icon name="x" size={14}/>
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ width: 36, height: 36, borderRadius: 10, background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)", color: "var(--fg-2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon name="upload" size={16}/>
                      </span>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{isEdit ? "Selecione o novo arquivo" : "Arraste o arquivo aqui ou clique para selecionar"}</div>
                      <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{tipo === "Vídeo" ? "MP4 ou WebM · até 50 MB" : "PNG ou JPG · até 10 MB"}</div>
                    </>
                  )}
                  <input type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={e => {
                    const f = e.target.files?.[0] || null;
                    setArquivo(f);
                    // Ajusta o tipo conforme o arquivo escolhido
                    if (f?.type.startsWith("video/")) setTipo("Vídeo");
                    else if (f?.type.startsWith("image/")) setTipo("Imagem");
                  }}/>
                </label>
              )}
            </UploadField>

            {/* Parceiro */}
            <UploadField label="Parceiro" hint="Opcional — vincule a mídia a um parceiro existente.">
              {parceiroSelDisplay ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)", borderRadius: 10 }}>
                  <div className="brand-tag" style={{ background: parceiroSelDisplay.cor, width: 28, height: 28, fontSize: 11, borderRadius: 8 }}>{parceiroSelDisplay.logo}</div>
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{parceiroSelDisplay.nome}</span>
                  <button onClick={() => { setParceiro(""); setParceiroBusca(""); }} style={{ border: "none", background: "transparent", color: "var(--fg-3)", display: "flex", cursor: "default" }}>
                    <Icon name="x" size={14}/>
                  </button>
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <div className="input">
                    <Icon name="search" size={13} color="var(--fg-3)"/>
                    <input type="text" placeholder="Digite para buscar parceiro…" value={parceiroBusca} onChange={e => setParceiroBusca(e.target.value)}/>
                  </div>
                  {parceiroBusca && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, marginTop: 4, background: "var(--surface)", border: "0.5px solid var(--hairline-strong)", borderRadius: 10, boxShadow: "var(--shadow-md)", maxHeight: 200, overflowY: "auto", padding: 4 }}>
                      {parceirosFiltrados.length === 0
                        ? <div style={{ padding: 12, fontSize: 12, color: "var(--fg-3)", textAlign: "center" }}>Nenhum parceiro encontrado</div>
                        : parceirosFiltrados.map(p => {
                            const nome = p.company || p.companyName || "?";
                            const logo = nome.replace(/\s+/g,"").slice(0,2).toUpperCase();
                            const cor  = p.brandColor || "#1f3a7a";
                            return (
                              <button key={p._id} onClick={() => { setParceiro(String(p._id)); setParceiroBusca(""); }}
                                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "8px 10px", background: "transparent", border: "none", borderRadius: 6, cursor: "default", font: "inherit", color: "inherit", textAlign: "left" }}
                                onMouseEnter={e => e.currentTarget.style.background = "var(--surface-sunken)"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <div className="brand-tag" style={{ background: cor, width: 24, height: 24, fontSize: 10, borderRadius: 7 }}>{logo}</div>
                                <span style={{ fontSize: 13 }}>{nome}</span>
                              </button>
                            );
                          })
                      }
                    </div>
                  )}
                </div>
              )}
            </UploadField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <UploadField label="Duração" hint="Tempo de exibição em segundos.">
                <div className="input">
                  <input type="number" min={1} max={120} value={duracao} onChange={e => setDuracao(Number(e.target.value) || 0)}/>
                  <span style={{ color: "var(--fg-3)", fontSize: 12 }}>seg</span>
                </div>
              </UploadField>
              <UploadField label="Data fim" hint="Opcional — defina expiração.">
                <div className="input">
                  <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}/>
                  <Icon name="calendar" size={13} color="var(--fg-3)"/>
                </div>
              </UploadField>
            </div>

            {/* Rotação */}
            <UploadField label="Rotação" hint={`Mídia girada ${rotacao}° → efetiva ${efetiva}° na tela vertical.`}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)", borderRadius: 10 }}>
                <button onClick={() => setRotacao((rotacao - 90 + 360) % 360)} style={{ width: 32, height: 32, borderRadius: 8, border: "0.5px solid var(--hairline)", background: "var(--surface)", color: "var(--fg)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "default" }} title="Girar -90°">
                  <Icon name="rotate" size={14} stroke={2}/>
                </button>
                <button onClick={() => setRotacao((rotacao + 90) % 360)} style={{ width: 32, height: 32, borderRadius: 8, border: "0.5px solid var(--hairline)", background: "var(--surface)", color: "var(--fg)", display: "flex", alignItems: "center", justifyContent: "center", transform: "scaleX(-1)", cursor: "default" }} title="Girar +90°">
                  <Icon name="rotate" size={14} stroke={2}/>
                </button>
                <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, fontSize: 16, letterSpacing: "-0.01em", paddingLeft: 4 }}>{rotacao}°</div>
                <div style={{ flex: 1 }}/>
                <div style={{ fontSize: 11, color: "var(--fg-3)" }}>efetiva <b style={{ color: "var(--fg-2)" }}>{efetiva}°</b></div>
              </div>
            </UploadField>

            {/* Ativo toggle */}
            <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)", borderRadius: 10, cursor: "default" }}>
              <div onClick={() => setAtivo(!ativo)} style={{ width: 36, height: 20, borderRadius: 999, background: ativo ? "var(--accent)" : "var(--surface-sunken)", border: "0.5px solid var(--hairline)", position: "relative", transition: "background .15s ease", flexShrink: 0, cursor: "default" }}>
                <div style={{ position: "absolute", top: 2, left: ativo ? 17 : 2, width: 15, height: 15, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left .15s ease" }}/>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>Mídia ativa</div>
                <div style={{ fontSize: 11, color: "var(--fg-3)" }}>Disponível imediatamente para uso em campanhas</div>
              </div>
            </label>

            {/* Progress */}
            {uploading && (
              <div style={{ background: "var(--surface-sunken)", border: "0.5px solid var(--hairline)", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 8 }}>
                  <span>Enviando arquivo…</span>
                  <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{progress}%</span>
                </div>
                <div style={{ height: 4, background: "var(--hairline)", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${progress}%`, background: "var(--accent)", borderRadius: 2, transition: "width .2s ease" }}/>
                </div>
              </div>
            )}

          </div>

          {/* Preview column */}
          <div style={{ background: "var(--surface-sunken)", borderLeft: "0.5px solid var(--hairline)", padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14, overflow: "hidden", minHeight: 0 }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: 10.5, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Preview da tela</div>
              <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 2 }}>Como vai aparecer no condomínio</div>
            </div>
            {/* Espaço livre (medido) — a TV cresce até preencher, sem rolar; ~56px são o selo e a legenda */}
            <div ref={previewAreaRef} style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <UploadPreview formato={formato} tipo={tipo} nome={nome} parceiro={parceiroSelDisplay} arquivo={arquivo} rotacao={rotacao} publicUrl={edit?.publicUrl}
                width={previewBox.w > 0 ? Math.min(previewBox.w, (previewBox.h - 56 - 10) * TV_VW / TV_VH + 10) : 200}/>
            </div>
            <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 6, padding: "10px 12px", background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 10, fontSize: 11.5 }}>
              <PreviewRow k="Tipo"      v={tipo}/>
              <PreviewRow k="Formato"   v={formato}/>
              <PreviewRow k="Dimensão"  v={edit?.dim || (formato === "Footer Bar" ? "1080×120" : formato === "Meia Tela" ? "—" : "1080×1920")}/>
              <PreviewRow k="Duração"   v={`${duracao}s`}/>
              <PreviewRow k="Rotação"   v={`${rotacao}° → efetiva ${efetiva}°`}/>
              <PreviewRow k="Parceiro"  v={parceiroSelDisplay?.nome || "—"}/>
              <PreviewRow k="Status"    v={
                <span className={`badge ${ativo ? "badge-green" : "badge-gray"}`} style={{ padding: "2px 7px", fontSize: 10 }}>
                  <span className="badge-dot"/>{ativo ? "Ativo" : "Inativo"}
                </span>
              }/>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
          <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}>{isEdit ? <>Editando <b style={{ color: "var(--fg-2)" }}>{edit.nome}</b></> : <><span style={{ color: "var(--red)" }}>*</span> campos obrigatórios</>}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose} disabled={uploading}>Cancelar</button>
            {isEdit ? (
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={!nome || uploading} style={{ opacity: !nome || uploading ? 0.5 : 1, background: "var(--purple)", borderColor: "var(--purple)" }}>
                <Icon name="edit" size={14}/> {uploading ? `Salvando… ${progress}%` : "Salvar alterações"}
              </button>
            ) : (
              <button className="btn btn-primary" onClick={handleUpload} disabled={!nome || !arquivo || uploading} style={{ opacity: !nome || !arquivo || uploading ? 0.5 : 1 }}>
                <Icon name="upload" size={14}/> {uploading ? `Enviando… ${progress}%` : "Fazer upload"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const FORMATOS_FILTER = ["Todos os formatos", "Tela Cheia", "Meia Tela", "Footer Bar"];

export default function MidiasList() {
  const [rawAssets,  setRawAssets]  = useState([]);
  const [partners,   setPartners]   = useState([]);
  const [usageMap,   setUsageMap]   = useState({});
  const [loading,    setLoading]    = useState(true);
  const [query,      setQuery]      = useState("");
  const [formato,    setFormato]    = useState("Todos os formatos");
  const [parceiro,   setParceiro]   = useState("all");
  const [tab,        setTab]        = useState("all");
  const [view,       setView]       = useState("list");
  const [selected,   setSelected]   = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [updatingStatusIds, setUpdatingStatusIds] = useState(new Set());

  const token = localStorage.getItem("token");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [aRes, pRes, uRes] = await Promise.all([
        fetch(`${API_URL}/admin/media-assets`, { headers }),
        fetch(`${API_URL}/admin/partners?limit=500`, { headers }),
        fetch(`${API_URL}/admin/campaign-items/by-media`, { headers }),
      ]);
      const aData = await aRes.json();
      const pData = await pRes.json();
      setRawAssets(aData.mediaAssets || []);
      setPartners(pData.items || []);
      if (uRes.ok) {
        const uData = await uRes.json();
        const map = {};
        // campanhas
        Object.entries(uData.usage || {}).forEach(([id, campaigns]) => {
          if (!map[id]) map[id] = [];
          campaigns.forEach(c => map[id].push({ kind: "campaign", ...c }));
        });
        // comunicados
        Object.entries(uData.noticeUsage || {}).forEach(([id, notices]) => {
          if (!map[id]) map[id] = [];
          notices.forEach(n => map[id].push({ kind: "notice", ...n }));
        });
        // rodapé das telas — uma entrada por mídia com todas as telas
        Object.entries(uData.footerUsage || {}).forEach(([id, screens]) => {
          if (!map[id]) map[id] = [];
          map[id].push({ kind: "footer", screenNames: screens.map(sc => sc.screenName) });
        });
        setUsageMap(map);
      }
    } catch (e) {
      console.error("MidiasList load error:", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const pMap = useMemo(() => Object.fromEntries(partners.map(p => [String(p._id), p])), [partners]);
  const assets = useMemo(() => rawAssets.map(m => mapAsset(m, pMap, usageMap)), [rawAssets, pMap, usageMap]);

  // KPIs
  const total     = assets.length;
  const exibindo  = assets.filter(m => m.status === "ativo" && m.usos.length).length;
  const semUso    = assets.filter(m => !m.usos.length).length;
  const expirando = assets.filter(m => {
    if (!m.fim || m.status !== "ativo") return false;
    const [d, mo, y] = m.fim.split("/").map(Number);
    return (new Date(y, mo - 1, d) - new Date()) / 86400000 <= 30;
  }).length;

  const parceirosAll = [...new Set(assets.filter(m => m.parceiro).map(m => m.parceiro))];

  const filtered = assets.filter(m => {
    if (formato !== "Todos os formatos" && m.formato !== formato) return false;
    if (parceiro === "sem" && m.parceiro) return false;
    if (parceiro !== "all" && parceiro !== "sem" && m.parceiro !== parceiro) return false;
    if (tab === "exibindo"  && !(m.status === "ativo" && m.usos.length)) return false;
    if (tab === "expiring") {
      if (!m.fim || m.status !== "ativo") return false;
      const [d, mo, y] = m.fim.split("/").map(Number);
      const expiryDate = new Date(y, mo - 1, d);
      const now = new Date();
      const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      if (!(expiryDate >= now && expiryDate <= in30)) return false;
    }
    if (tab === "agendada"  && m.status !== "agendada") return false;
    if (tab === "expirada"  && m.status !== "expirada") return false;
    if (tab === "semuso"    && m.usos.length) return false;
    if (query && !`${m.nome} ${m.parceiro||""} ${m.tipo} ${m.formato}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  async function handleDelete(id) {
    if (!window.confirm("Excluir esta mídia permanentemente?")) return;
    try {
      const r = await fetch(`${API_URL}/admin/media-assets/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json()).message || "Erro ao excluir");
      setSelected(null);
      loadAll();
    } catch (e) { alert(e.message); }
  }

  function isUpdatingStatus(mediaId) {
    return updatingStatusIds.has(mediaId);
  }

  async function handleMediaStatusChange(media, nextActive) {
    if (!media || media.activeRaw === nextActive) return;

    const mediaId = media._id;

    setUpdatingStatusIds((prev) => {
      const next = new Set(prev);
      next.add(mediaId);
      return next;
    });

    // Atualização otimista do ativo para refletir imediatamente na tabela.
    setRawAssets((prev) =>
      prev.map((item) =>
        String(item._id) === mediaId ? { ...item, active: nextActive } : item
      )
    );

    try {
      const r = await fetch(`${API_URL}/admin/media-assets/${mediaId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ active: nextActive }),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao atualizar status da mídia");
      }
    } catch (e) {
      console.error(e);
      // Reverte otimista em caso de erro.
      setRawAssets((prev) =>
        prev.map((item) =>
          String(item._id) === mediaId ? { ...item, active: media.activeRaw } : item
        )
      );
      alert(e.message || "Erro ao atualizar status da mídia");
    } finally {
      setUpdatingStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(mediaId);
        return next;
      });
    }
  }

  return (
    <div className="midias-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Mídias Indoor</h1>
          <div className="page-sub">
            <span className="live-dot"/>
            <span><b style={{ color: "var(--fg)" }}>{exibindo}</b> em exibição · biblioteca com {total} criativos</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={loadAll} disabled={loading}><Icon name="refresh" size={14}/> Atualizar</button>
          <button className="btn btn-primary" onClick={() => setUploadOpen(true)}><Icon name="plus" size={14}/> Upload Mídia</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid-kpis">
        <div className="card kpi">
          <div className="kpi-label"><span>Total de mídias</span><span className="kpi-icon"><Icon name="film" size={14}/></span></div>
          <div className="kpi-value">{loading ? "—" : total}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{assets.filter(m => m.tipo === "Vídeo").length} vídeos · {assets.filter(m => m.tipo === "Imagem").length} imagens</span></div>
        </div>
        <div className="card kpi">
          <div className="kpi-label"><span>Em exibição agora</span><span className="kpi-icon green"><Icon name="eye" size={14}/></span></div>
          <div className="kpi-value" style={{ color: "var(--green)" }}>{loading ? "—" : exibindo}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>de {total} no acervo</span></div>
        </div>
        <div className="card kpi">
          <div className="kpi-label"><span>Expirando em 30 dias</span><span className="kpi-icon amber"><Icon name="clock" size={14}/></span></div>
          <div className="kpi-value" style={{ color: expirando ? "var(--amber)" : "var(--fg-3)" }}>{loading ? "—" : expirando}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{expirando ? "renovar antes do vencimento" : "nenhuma expirando"}</span></div>
        </div>
        <div className="card kpi">
          <div className="kpi-label"><span>Sem uso</span><span className="kpi-icon purple"><Icon name="alert" size={14}/></span></div>
          <div className="kpi-value" style={{ color: semUso ? "var(--purple)" : "var(--fg-3)" }}>{loading ? "—" : semUso}</div>
          <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{semUso ? "sem campanha, comunicado ou rodapé" : "todas em uso"}</span></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginTop: 14, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <div className="input-wrap" style={{ flex: 1, minWidth: 280 }}>
            <label className="input-label">Buscar</label>
            <div className="input">
              <Icon name="search" size={14} color="var(--fg-3)"/>
              <input className="search-bare-input" type="text" placeholder="Filtrar por nome, parceiro, tipo…" value={query} onChange={e => setQuery(e.target.value)}/>
              {query && <button onClick={() => setQuery("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", cursor: "default" }}><Icon name="x" size={14}/></button>}
            </div>
          </div>
          <div className="input-wrap" style={{ minWidth: 180 }}>
            <label className="input-label">Formato</label>
            <div className="input">
              <select value={formato} onChange={e => setFormato(e.target.value)}>
                {FORMATOS_FILTER.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <Icon name="arrowDown" size={12} color="var(--fg-3)"/>
            </div>
          </div>
          <div className="input-wrap" style={{ minWidth: 200 }}>
            <label className="input-label">Parceiro</label>
            <div className="input">
              <select value={parceiro} onChange={e => setParceiro(e.target.value)}>
                <option value="all">Todos os parceiros</option>
                <option value="sem">(sem parceiro)</option>
                {parceirosAll.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <Icon name="arrowDown" size={12} color="var(--fg-3)"/>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 14, flexWrap: "wrap" }}>
          {[
            { id: "all",      label: "Todas",       n: total },
            { id: "exibindo", label: "Em exibição", n: exibindo },
            { id: "agendada", label: "Agendadas",   n: assets.filter(m => m.status === "agendada").length },
            { id: "expirada", label: "Expiradas",   n: assets.filter(m => m.status === "expirada").length },
            { id: "semuso",   label: "Sem uso",     n: semUso },
          ].map(c => (
            <button key={c.id} className={`chip ${tab === c.id ? "on" : ""}`} onClick={() => setTab(c.id)}>
              {c.label}
              <span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.7 }}>{c.n}</span>
            </button>
          ))}
        </div>
      </div>

      {/* List/Grid */}
      <div className="card" style={{ marginTop: 14, padding: "16px 0 8px" }}>
        <div className="card-h" style={{ padding: "0 20px 14px" }}>
          <h3>Biblioteca de criativos <span className="meta">{filtered.length} de {total}</span></h3>
          <div className="seg">
            <button className={view === "list" ? "on" : ""} onClick={() => setView("list")}><Icon name="list" size={12}/></button>
            <button className={view === "grid" ? "on" : ""} onClick={() => setView("grid")}><Icon name="layoutGrid" size={12}/></button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>Carregando mídias…</div>
        ) : view === "list" ? (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 20, width: 80 }}>Preview</th>
                  <th>Nome</th>
                  <th>Parceiro</th>
                  <th style={{ width: 130 }}>Origem</th>
                  <th style={{ width: 96 }}>Status</th>
                  <th style={{ width: 150 }}>Formato</th>
                  <th style={{ width: 110 }}>Criado</th>
                  <th style={{ width: 100 }}>Dt. fim</th>
                  <th>Uso</th>
                  <th style={{ width: 120, paddingRight: 20 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m._id} onClick={() => setSelected(m)} style={{ cursor: "default" }}>
                    <td style={{ paddingLeft: 20 }}><MidiaThumb m={m} size={48}/></td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontWeight: 600 }}>{m.nome}</span>
                        <span style={{ color: "var(--fg-3)", fontSize: 11.5 }}>{m.tipo} · {m.orient}°</span>
                      </div>
                    </td>
                    <td>
                      {m.parceiro ? (
                        <div className="client-cell">
                          <div className="brand-tag" style={{ background: m.parceiroCor || "#1f3a7a", width: 24, height: 24, fontSize: 10, borderRadius: 7 }}>{m.parceiroLogo}</div>
                          <span style={{ color: "var(--fg-2)" }}>{m.parceiro}</span>
                        </div>
                      ) : <span style={{ color: "var(--fg-4)", fontStyle: "italic" }}>(sem parceiro)</span>}
                    </td>
                    <td>
                      {m.origem === "condo" ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span className="badge badge-amber" style={{ width: "fit-content" }}>Síndico</span>
                          {m.condoName && <span style={{ color: "var(--fg-3)", fontSize: 11 }}>{m.condoName}</span>}
                        </div>
                      ) : (
                        <span className="badge badge-gray" style={{ width: "fit-content" }}>Admin</span>
                      )}
                    </td>
                    <td>
                      <div className={`badge ${m.activeRaw ? "badge-green" : "badge-gray"}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, paddingRight: 6, opacity: isUpdatingStatus(m._id) ? 0.7 : 1, position: "relative", cursor: isUpdatingStatus(m._id) ? "not-allowed" : "pointer" }} onClick={e => e.stopPropagation()}>
                        <span className="badge-dot"/>
                        <span style={{ fontWeight: 600 }}>{m.activeRaw ? "Ativo" : "Desativado"}</span>
                        <Icon name="arrowDown" size={10} color="currentColor"/>
                        <select
                          aria-label="Alterar status da mídia"
                          value={m.activeRaw ? "active" : "inactive"}
                          onChange={e => handleMediaStatusChange(m, e.target.value === "active")}
                          disabled={isUpdatingStatus(m._id)}
                          style={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            border: "none",
                            cursor: isUpdatingStatus(m._id) ? "not-allowed" : "pointer",
                          }}
                        >
                          <option value="active">Ativo</option>
                          <option value="inactive">Desativado</option>
                        </select>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {renderLayoutBadge(m.compatibleLayouts?.length ? m.compatibleLayouts : classifyLayouts(m.width, m.height))}
                        <span className="num" style={{ fontSize: 11, color: "var(--fg-3)" }}>{m.dim}</span>
                      </div>
                    </td>
                    <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{m.criado}</td>
                    <td className="num" style={{ color: m.fim ? "var(--fg-2)" : "var(--fg-4)", fontSize: 12 }}>{m.fim || "—"}</td>
                    <td>
                      {m.usos.length ? (() => {
                        const u = m.usos[0];
                        const isFooter = u.kind === "footer";
                        const isCampaign = !isFooter && (u.kind === "campaign" || u.campaignName);
                        const sn = u.screenNames || [];
                        const title = isFooter ? "Rodapé das telas" : isCampaign ? (u.campaignName || "Campanha") : (u.noticeTitle || "Comunicado");
                        const scope = isFooter
                          ? (sn.length === 1 ? sn[0] : `${sn.length} telas`)
                          : isCampaign
                          ? (sn.length === 0 ? "—"
                            : sn[0] === "Todas as telas" ? "Todas as telas"
                            : /condomínio|cidade/.test(sn[0]) ? sn[0]
                            : `${sn.length} tela${sn.length !== 1 ? "s" : ""}`)
                          : (u.scopeLabel || "—");
                        const icon = isFooter ? "image" : isCampaign ? "zap" : "file";
                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 500, color: "var(--fg-1)" }}>
                              <Icon name={icon} size={11} color={isCampaign ? "var(--accent)" : "var(--fg-3)"}/>
                              {title}
                            </span>
                            <span style={{ color: "var(--fg-3)", fontSize: 11, paddingLeft: 16 }}>{scope}</span>
                            {m.usos.length > 1 && <span style={{ color: "var(--fg-3)", fontSize: 10, paddingLeft: 16 }}>+{m.usos.length - 1} uso{m.usos.length - 1 !== 1 ? "s" : ""}</span>}
                          </div>
                        );
                      })() : <span style={{ color: "var(--fg-4)", fontSize: 12 }}>Sem uso</span>}
                    </td>
                    <td style={{ paddingRight: 20 }}>
                      <div className="midia-actions" onClick={e => e.stopPropagation()}>
                        <button className="midia-act blue" title="Ver detalhes" onClick={() => setSelected(m)}><Icon name="eye" size={14}/></button>
                        <button className="midia-act purple" title="Editar" onClick={() => setEditTarget(m)}><Icon name="edit" size={14}/></button>
                        <button className="midia-act red" title="Excluir" onClick={() => handleDelete(m._id)}><Icon name="trash" size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={11} style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>
                    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <Icon name="image" size={24} color="var(--fg-4)"/>
                      <div>Nenhuma mídia encontrada</div>
                      <button className="btn" style={{ marginTop: 8 }} onClick={() => { setQuery(""); setTab("all"); setFormato("Todos os formatos"); setParceiro("all"); }}>Limpar filtros</button>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: "0 20px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
            {filtered.map(m => <MidiaCard key={m._id} m={m} onClick={() => setSelected(m)}/>)}
            {filtered.length === 0 && <div style={{ gridColumn: "1 / -1", padding: 60, textAlign: "center", color: "var(--fg-3)" }}>Nenhuma mídia encontrada</div>}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: "0.5px solid var(--hairline)", marginTop: 8 }}>
          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>Mostrando {filtered.length} de {total} mídias</span>
        </div>
      </div>

      <div style={{ height: 24 }}/>

      <MidiaDetalheDrawer m={selected} onClose={() => setSelected(null)} onDelete={handleDelete} onEdit={m => { setSelected(null); setEditTarget(m); }}/>
      <UploadMidiaModal open={uploadOpen} onClose={() => setUploadOpen(false)} onSuccess={loadAll} partners={partners} token={token}/>
      <UploadMidiaModal open={!!editTarget} edit={editTarget} onClose={() => setEditTarget(null)} onSuccess={loadAll} partners={partners} token={token}/>
    </div>
  );
}
